"use client";

/**
 * ServiceShowcase Component - Category-Based Organization with Service Targeting
 * 
 * This component uses a clean category-based structure for service organization:
 * 
 * URL Patterns Supported:
 * - /services/auto-services?category=mechanic (category-based filtering)
 * - /services/auto-services?category=tires (category-based filtering)
 * - /services/auto-services?service=oil-change&category=mechanic (specific service targeting)
 * - /services/auto-services?service=tire-installation&category=tires (specific service targeting)
 * 
 * Benefits:
 * - Clean, organized category structure
 * - Specific service targeting with overlay auto-open
 * - Better URLs and improved SEO
 * - Enhanced scalability for future service additions
 * - Simplified codebase without legacy support
 */

import { useEffect, useMemo, useState, useRef } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import CustomButton from "../ui/custom-button";
import { ArrowRight, ArrowLeft, X } from "lucide-react";
import { CategoryFilterBar } from "./category-filter-bar";
import { useSearchParams, useRouter } from "next/navigation";
import { ServiceFilter } from "@/types/location";

// Hook to detect mobile view
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile;
};

// Service category structure for better organization
export interface ServiceCategory {
  id: string;
  name: string;
  slug: string;
  subcategories: ServiceFilter[];
}

// New grouped category structure
const serviceCategories: ServiceCategory[] = [
  {
    id: "automotive-maintenance",
    name: "Automotive Maintenance",
    slug: "automotive-maintenance",
    subcategories: [
      { id: "all", name: "All Services", slug: "all" },
      { id: "mechanic", name: "Mechanic Services", slug: "mechanic" },
      { id: "tires", name: "Tire Services", slug: "tires" },
    ]
  }
];

// Utility function to get category by slug or id
export const getCategoryBySlug = (slug: string): ServiceCategory | undefined => {
  return serviceCategories.find(cat => cat.slug === slug || cat.id === slug);
};

// Utility function to get all available categories
export const getAllCategories = (): ServiceCategory[] => {
  return serviceCategories;
};

// Utility function to create a URL for a specific service category
export const createCategoryURL = (basePath: string, categorySlug: string): string => {
  return `${basePath}?category=${categorySlug}`;
};

// Utility function to create a URL for a specific service within a category
export const createServiceURL = (basePath: string, serviceId: string, categorySlug: string): string => {
  return `${basePath}?service=${serviceId}&category=${categorySlug}`;
};
interface ServiceShowcaseItem {
  id: string;
  title: string;
  image: string;
  description?: string;
  href?: string;
  type?: string; // This should match the id in serviceFilters
  details?: {
    title?: string;
    items?: string[];
    footer?: string;
  };
}

interface ServiceShowcaseProps {
  title: string;
  subtitle: string;
  href: string;
  services: ServiceShowcaseItem[];
  className?: string;
  type?: string; // Default to "auto-services"
  category?: ServiceCategory; // Optional category for enhanced organization
}

export function ServiceShowcase({
  title,
  subtitle,
  href,
  services,
  className,
  type = "collision-repair",
  category,
}: ServiceShowcaseProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const categoryParam = searchParams?.get("category");
  const serviceParam = searchParams?.get("service");
  const sectionRef = useRef<HTMLElement>(null);
  const [activeService, setActiveService] = useState(0);
  const [activeOverlay, setActiveOverlay] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState("all");
  const isMobile = useIsMobile();

  // Show 3 items on mobile, 6 items (2 rows × 3 columns) on desktop
  const itemsPerPage = isMobile ? 3 : 6;

  // Use provided category or default to automotive maintenance
  const activeCategory = category || serviceCategories[0];
  const availableFilters = activeCategory.subcategories;

  useEffect(() => {
    // Handle specific service parameter (e.g., ?service=oil-change&category=mechanic)
    if (serviceParam) {
      const targetService = services.find(service => service.id === serviceParam);
      if (targetService) {
        // Set the active filter based on the target service's type or provided category
        const serviceType = targetService.type;
        const matchingFilter = availableFilters.find(filter => filter.id === serviceType);
        const filterToUse = categoryParam || (matchingFilter ? matchingFilter.slug : "all");

        setActiveFilter(filterToUse);

        // Find which page the target service is on in the filtered results
        const filteredServicesList = filterToUse !== "all"
          ? services.filter(service => service.type === availableFilters.find(f => f.slug === filterToUse)?.id)
          : services;

        const serviceIndex = filteredServicesList.findIndex(service => service.id === targetService.id);
        const targetPage = serviceIndex >= 0 ? Math.floor(serviceIndex / itemsPerPage) : 0;

        // Set the active service page
        setActiveService(targetPage);

        // Open the overlay for the target service
        setActiveOverlay(targetService.id);

        // Scroll to this component
        setTimeout(() => {
          sectionRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }, 100);
        return;
      }
    }

    // Handle category parameter (e.g., ?category=mechanic)
    if (categoryParam) {
      const matchingFilter = availableFilters.find(filter => filter.slug === categoryParam || filter.id === categoryParam);
      if (matchingFilter) {
        setActiveFilter(matchingFilter.slug);
        setActiveService(0);
        setActiveOverlay(null);

        // Scroll to this component
        setTimeout(() => {
          sectionRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }, 100);
      } else {
        setActiveFilter("all");
        setActiveService(0);
        setActiveOverlay(null);
      }
    } else {
      setActiveFilter("all");
      setActiveService(0);
      setActiveOverlay(null);
    }
  }, [categoryParam, serviceParam, services, availableFilters]);

  useEffect(() => {
    // Reset active service and overlay when filter changes (but not when initially loading with category/service)
    if (!categoryParam && !serviceParam) {
      setActiveService(0);
      setActiveOverlay(null);
    }
  }, [activeFilter, categoryParam, serviceParam]);

  const filteredServices = useMemo(() => {
    if (activeFilter === "all" || type === "collision-repair") {
      return services;
    }

    const filterName = availableFilters.find((f) => f.slug === activeFilter)?.id;

    if (!filterName) {
      return services;
    }

    return services.filter((service) => service.type === filterName);
  }, [activeFilter, services, availableFilters]);

  // Recalculate pagination when screen size changes
  const totalPages = useMemo(() => Math.ceil(filteredServices.length / itemsPerPage), [filteredServices.length, itemsPerPage]);

  // Reset to first page if current page is beyond the new total pages (e.g., when switching from mobile to desktop)
  useEffect(() => {
    if (activeService >= totalPages && totalPages > 0) {
      setActiveService(0);
    }
  }, [totalPages, activeService]);

  const visibleServices = filteredServices.slice(
    activeService * itemsPerPage,
    activeService * itemsPerPage + itemsPerPage
  );

  const canGoPrev = activeService > 0;
  const canGoNext = activeService < totalPages - 1;

  const handlePrev = () => {
    setActiveService((prev) => (prev > 0 ? prev - 1 : totalPages - 1));
    setActiveOverlay(null); // Reset overlay when changing page
  };

  const handleNext = () => {
    setActiveService((prev) => (prev + 1) % totalPages);
    setActiveOverlay(null); // Reset overlay when changing page
  };

  const handleFilterChange = (newFilter: string) => {
    setActiveFilter(newFilter);
    setActiveService(0);
    setActiveOverlay(null);

    // Update the URL with the new category parameter and clear service parameter
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete('service'); // Clear specific service when changing filter

    if (newFilter === "all") {
      // Remove category parameter when "all" is selected
      currentUrl.searchParams.delete('category');
    } else {
      // Set the new category parameter
      currentUrl.searchParams.set('category', newFilter);
    }
    router.replace(currentUrl.pathname + currentUrl.search, { scroll: false });
  };

  const toggleOverlay = (id: string) => {
    setActiveOverlay(activeOverlay === id ? null : id);
  };

  return (
    <section
      ref={sectionRef}
      className={cn("py-16 relative font-outfit bg-site", className)}
    >
      <div className=" mx-4 md:mx-16 lg:mx-20">
        {/* Title and CTA Section */}
        <div className="flex flex-col  md:flex-row justify-between items-start lg:items-center mb-12">
          <div className="">
            <h2 className="text-3xl md:text-4xl font-bold text-yellow-500 mb-2">
              {title}
            </h2>
            <p className="text-white text-lg">{subtitle}</p>
          </div>
          <div className="hidden lg:flex  justify-end flex-wrap gap-4 ">
            <CustomButton
              variant="outline"
              className="border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black rounded-full px-6"
            >
              <a href={href}>Find Location</a>
            </CustomButton>
          </div>
        </div>
        {type === "auto-services" && (
          <CategoryFilterBar
            filters={availableFilters}
            activeFilter={activeFilter}
            onFilterChange={handleFilterChange}
          />
        )}

        {/* Service Categories */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {visibleServices.map((service) => (
            <div
              key={service.id}
              className="relative rounded-3xl overflow-hidden h-[250px] cursor-pointer group"
              onClick={() => toggleOverlay(service.id)}
            >
              {/* Background Image */}
              <Image
                src={service.image || "/placeholder.svg"}
                alt={service.title}
                fill
                className={cn(
                  "object-cover transition-all duration-500",
                  activeOverlay === service.id
                    ? "blur(4px) brightness(0.4)"
                    : "group-hover:scale-105"
                )}
                style={{
                  filter:
                    activeOverlay === service.id
                      ? "blur(4px) brightness(0.4)"
                      : "none",
                }}
              />

              {/* Title */}
              <div className="absolute inset-0 flex items-center justify-center">
                <h3
                  className={cn(
                    "text-white font-bold text-xl text-center transition-all duration-500",
                    activeOverlay === service.id
                      ? "opacity-0 scale-90"
                      : "opacity-100 scale-100"
                  )}
                >
                  {service.title}
                </h3>
              </div>

              {/* Overlay */}
              <div
                className={cn(
                  "absolute inset-0 flex flex-col items-center justify-center p-6 text-white transition-all duration-500",
                  activeOverlay === service.id
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-4 pointer-events-none"
                )}
              >
                <div className="text-center space-y-4">
                  <h3 className="text-xl font-semibold mb-3">
                    {service.details?.title || service.title}
                  </h3>
                  <div className="space-y-2 text-sm">
                    {service.details?.items?.map((item, i) => (
                      <p key={i}>• {item}</p>
                    )) || (
                        <>
                          <p>• Professional service</p>
                          <p>• Expert technicians</p>
                          <p>• Quality assurance</p>
                        </>
                      )}
                  </div>
                  <p className="text-xs mt-4 opacity-90">
                    {service.details?.footer ||
                      "Trusted by thousands of customers"}
                  </p>
                </div>

                {/* Close button */}
                <button aria-label="close button"
                  className="absolute top-4 right-4 p-1 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveOverlay(null);
                  }}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
            </div>
          ))}
        </div>

        <div className="flex gap-5  justify-center mt-10 items-center">
          {/* Left Button */}
          <button aria-label="Goto previous service"
            onClick={handlePrev}
            disabled={!canGoPrev}
            className={cn(
              "rounded-full  flex  w-12 h-12  items-center justify-center text-black transition-colors",
              canGoPrev
                ? "bg-yellow-500 hover:bg-yellow-600"
                : "bg-yellow-500/50 cursor-not-allowed"
            )}
          >
            <ArrowLeft className="h-5 w-5" />
          </button>

          <button aria-label="Goto next service"
            onClick={handleNext}
            disabled={!canGoNext}
            className={cn(
              "rounded-full flex w-12 h-12  items-center justify-center text-black transition-colors",
              canGoNext
                ? "bg-yellow-500 hover:bg-yellow-600"
                : "bg-yellow-500/50 cursor-not-allowed"
            )}
          >
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </section>
  );
}
